/**
 * The "add a chat or channel" control: a button that expands into a live-loaded,
 * multi-select list of the connected account's Graph options. Shared between the
 * account-wide default set (TeamsConnection) and a per-script override set
 * (ScriptNotifyTarget) — the fetch and the list are identical; only where a pick is
 * added differs.
 *
 * Already-selected destinations show as picked and disabled rather than disappearing —
 * removing one happens in the caller's own list of current destinations, not here.
 */

import { useState } from "react";

import type { TeamsTargetOption } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { Badge, Empty, Loading, Notice } from "@/components/ui";

const PAGE_SIZE = 8;

export function TeamsTargetPicker({
  selectedRefs,
  onAdd,
  busy,
  buttonLabel = "Add a chat or channel",
}: {
  /** graph_ref values already in the current set, so they show picked and disabled. */
  selectedRefs: Set<string>;
  onAdd: (option: TeamsTargetOption) => void | Promise<void>;
  busy?: boolean;
  buttonLabel?: string;
}) {
  const [picking, setPicking] = useState(false);
  const [options, setOptions] = useState<TeamsTargetOption[]>();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [error, setError] = useState<string>();
  const [adding, setAdding] = useState<string>();

  const load = async () => {
    setPicking(true);
    setVisibleCount(PAGE_SIZE);
    setError(undefined);
    try {
      setOptions(await endpoints.teamsTargetOptions());
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  const add = async (option: TeamsTargetOption) => {
    setAdding(option.graph_ref);
    setError(undefined);
    try {
      await onAdd(option);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setAdding(undefined);
    }
  };

  if (!picking) {
    return (
      <button type="button" className="button button--small" onClick={load}>
        {buttonLabel}
      </button>
    );
  }

  const visible = options?.slice(0, visibleCount) ?? [];
  const remaining = (options?.length ?? 0) - visible.length;

  return (
    <div className="stack" style={{ gap: 6 }}>
      {error && <Notice tone="danger">{error}</Notice>}
      {options === undefined ? (
        <Loading rows={3} label="Loading your chats and channels" />
      ) : options.length === 0 ? (
        <Empty title="No chats or channels found" />
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          {visible.map((option) => {
            const picked = selectedRefs.has(option.graph_ref);
            return (
              <button
                key={`${option.kind}:${option.graph_ref}`}
                type="button"
                className={`button ${picked ? "button--primary" : "button--ghost"}`}
                style={{ justifyContent: "flex-start", width: "100%" }}
                disabled={busy || picked || adding === option.graph_ref}
                onClick={() => add(option)}
              >
                {picked ? "✓ " : ""}
                <Badge>{option.kind}</Badge> {option.label}
              </button>
            );
          })}
          {remaining > 0 && (
            <button
              type="button"
              className="button button--small button--ghost"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            >
              Show {Math.min(remaining, PAGE_SIZE)} more ({remaining} left)
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        className="button button--small button--ghost"
        onClick={() => setPicking(false)}
      >
        Done
      </button>
    </div>
  );
}
